/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import { Canvas, StaticCanvas, FabricObject } from 'fabric';
import * as Sentry from '@sentry/react';

import { prepareHeaders, retryFetch } from '@app/app/lib/api';

import { serverPath } from '@app/utils/server-path';
import { cleanStateFromSnaplines } from '@app/widgets/canvas/utils/guidesnap/helpers';
import { analytics, getFlowFromUrl } from '@app/app/analytics';

import { FetchError, handleFetchError } from '@app/app/lib';

import {
  copyObjects,
  extractSlideMetadata,
  pasteObjects,
  isCanvasLocked,
  setCanvasBackgroundImage,
  findBackgroundObject,
} from '@app/widgets/canvas/utils';

import { wait } from '@lib/utils';

import { PlatformName } from '@app/app/analytics/types';

import { Layer, LayerObject } from '../types/types';
import { loadObjectsWithLayers } from '../utils';

import { CANVAS_DIMENSIONS, CANVAS_SCALE } from '../config/constants';

import { restoreOriginalOpacitiesInState } from '../utils/opacity';
import { checkIsEmptyThumbnailBase64 } from '../utils/thumbnail/checkIsEmptyThumbnailBase64';

import { canvasIndexedDB, PersistentStorage as IndexedDBStorage } from './canvasIndexedDB';
import { useLoggingStore } from './logger';
import { useSlideStore } from './slide';

// Типы для глобальной истории действий
export type StyleBoundaryAction = {
  type: 'style_boundary';
  boundaryType: 'style_changed' | 'restyling_done';
  styleId?: string;
  affectedSlides: string[]; // слайды которые затронуты рестайлингом
  originalSlides?: string[]; // изначальные слайды на момент рестайлинга
  timestamp: number;
  description: string;
};

export type GlobalHistoryAction =
  | StyleBoundaryAction
  | {
      type: 'slide_switch';
      slideId: string;
      timestamp: number;
    }
  | {
      type: 'slide_backup';
      slideId: string;
      backupIndex: number;
      timestamp: number;
    }
  | {
      type: 'slide_add';
      slideId: string;
      position: number;
      slideData?: any;
      timestamp: number;
    }
  | {
      type: 'slide_remove';
      slideId: string;
      slideData: any;
      position: number;
      timestamp: number;
    };

export type GlobalHistory = {
  actions: GlobalHistoryAction[];
  currentActionIndex: number;
};

// Типы для персистентности
type PersistedCanvasData = {
  backups: BackupState;
  currentStateIndex: number;
  metaData?: MetaData;
  thumbnail?: {
    smallImg: {
      status: 'pending' | 'loaded' | 'error';
      url: string;
    };
    bigImg: {
      status: 'pending' | 'loaded' | 'error';
      url: string;
    };
  };
  position?: number;
  // Время последнего обновления для очистки старых данных
  lastUpdated: number;
  // Сохраняем initialData для сравнения при следующей загрузке
  initialData?: any;
  // Тип стиля маркеров
  markerType?: string;
};

type WorkspacePersistentData = {
  canvasData: { [canvasId: string]: PersistedCanvasData };
  globalHistory: GlobalHistory;
  lastAccessed: number;
};

type BackupState = any[];

type LayersMap = { [key in Layer]: LayerObject[] };

type MetaData = Record<string, any> & {
  slide_width?: number;
  slide_height?: number;
  isHaveBackgroundImage?: boolean;
};

// Object movement state
interface MovementState {
  lastDirection: string | null;
  hasPendingChanges: boolean;
  activeObjects: string[]; // IDs of active objects
}

export type CanvasData = {
  initialData: any;
  fabricInstance: Canvas | null;
  element: HTMLElement | null;
  backups: BackupState;
  backupDepth: number;
  currentStateIndex: number;
  layersMap: { [key in Layer]: LayerObject[] };
  metaData?: MetaData;
  canvasDimensions?: { width: number; height: number };
  canvasScale?: number;
  savedLastVersionStatus: {
    isSaved: boolean;
    isSaving: boolean;
    error: string | null;
  };
  // Flag for checking the update status (set True if the slide is waiting for update)
  isPendingUpdate: boolean;
  staticCanvas?: {
    instance: StaticCanvas;
    containerElement: HTMLElement;
  };
  thumbnail?: {
    smallImg: {
      status: 'pending' | 'loaded' | 'error';
      url: string;
    };
    bigImg: {
      status: 'pending' | 'loaded' | 'error';
      url: string;
    };
  };
  isNeedUpdateFabricInstance: boolean;
  isNeedUpdateElement: boolean;
  isRegistered: boolean;
  isRelayout: boolean;
  isRepaint: boolean;
  // Object movement state
  movementState: MovementState;
  // Position (for sharing)
  position?: number;
  // Тип стиля маркеров (normal, adaptive и т.д.)
  markerType?: string;
  // Состояние блокировки канваса (для overlay)
  isLocked?: boolean;
  lockReason?: string;
};

export type CanvasDataList = {
  [key: string]: CanvasData;
};

type ThumbnailQueueItem = {
  canvasId: string;
  size: 'small' | 'big';
  forceRegenerate: boolean;
  priority: number;
  createdAt: number;
  useInitialDataOnly?: boolean;
};

type CanvasStoreState = {
  saveBackupTimers: Map<string, NodeJS.Timeout>;
  thumbnailTimers: Map<string, NodeJS.Timeout>;
  thumbnailQueue: ThumbnailQueueItem[];
  isProcessingThumbnail: boolean;
  isPendingUpdate: boolean;
  workspaceId: string;
  activeCanvasId: string | null;
  canvasData: CanvasDataList;
  buffer: {
    objects: any[]; // List of copied objects
    sourceCanvasId: string | null; // ID of the canvas from which the objects were copied
  };
  isUndoOrRedoInProgress: boolean;
  // Глобальная история действий
  globalHistory: GlobalHistory;
};

type CanvasActions = {
  setActiveCanvasId: (canvasId: string) => void;
  addCanvas: ({
    canvasId,
    initialData,
    fabricInstance,
    element,
    canvasScale,
    canvasDimensions,
    isRegistered,
    position,
  }: {
    canvasId: string;
    initialData: any;
    fabricInstance: Canvas | null;
    element: HTMLElement | null;
    backupDepth?: number;
    metaData?: MetaData;
    canvasScale?: number;
    canvasDimensions?: { width: number; height: number };
    isRegistered?: boolean;
    position?: number;
  }) => void;
  deleteCanvas: (canvasId: string) => void;
  getCanvasState: (canvasId: string) => CanvasData | null;
  getFabricInstance: (canvasId: string) => any | null;
  addBackup: (
    canvasId: string,
    state: any,
    sendToServer?: boolean,
    generateThumbnail?: boolean,
  ) => void;
  clearBackups: (canvasId: string) => void;
  undo: (canvasId: string, skipGlobalCheck?: boolean) => any | null;
  redo: (canvasId: string, skipGlobalCheck?: boolean) => any | null;
  addLayerObject: (canvasId: string, layer: Layer, object: LayerObject) => void;
  removeLayerObject: (canvasId: string, layer: Layer, object: LayerObject) => void;
  clearLayer: (canvasId: string, layer: Layer) => void;
  updateMetaData: (canvasId: string, meta: MetaData) => void;
  getMetaData: (canvasId: string) => Record<string, any> | null;
  copyToBuffer: (canvasId: string) => Promise<void>;
  pasteFromBuffer: (
    targetCanvasId: string,
    options?: {
      insertCoordinates?: { left: number; top: number };
      offsetX?: number;
      offsetY?: number;
      isNeedSetActivePastedObject?: boolean; // Activate pasted objects after pasting (true by default)
    },
  ) => Promise<void>;
  closeSelectionFrame: () => void;
  clearStore: () => void;
  saveBackupToServer: (canvasId: string, state: any) => void;
  setWorkspaceId: (workspaceId: string) => void;
  removeCanvas: (canvasId: string) => void;
  updateCanvasScale: (canvasId: string, scale: number) => void;
  updateCanvasDimensions: (
    canvasId: string,
    dimensions: { width: number; height: number },
    scale?: number,
  ) => void;
  // methods for object movement management
  updateMovementState: (canvasId: string, objects: FabricObject[], direction: string) => boolean;
  finishObjectMovement: (canvasId: string, forceBackup?: boolean) => boolean;
  checkActiveObjectsChanged: (canvasId: string) => boolean;
  updateFabricInstance: (canvasId: string, fabricInstance: Canvas) => void;
  updateCanvasElement: (canvasId: string, canvasElement: HTMLElement) => void;
  setIsNeedUpdateFabricInstance: (canvasId: string, isNeedUpdate: boolean) => void;
  setIsRelayout: (canvasId: string, isRelayout: boolean) => void;
  setIsRepaint: (canvasId: string, isRepaint: boolean) => void;
  cleanUpCanvas: (canvasId: string, clearTimers?: boolean) => void;
  // methods for work with thumbnails
  // ОСНОВНАЯ ФУНКЦИЯ ГЕНЕРАЦИИ ТУМБОВ
  // useInitialDataOnly: флаг для принудительного использования initialData вместо backup данных
  // Используется после рестайлинга когда нужно сгенерировать тумбы по новому дизайну
  generateThumbnail: (
    canvasId: string,
    size: 'small' | 'big',
    forceRegenerate?: boolean,
    useInitialDataOnly?: boolean, // НОВЫЙ ПАРАМЕТР для обхода backup данных
    retryAttempt?: number, // Параметр для отслеживания попыток регенерации
  ) => Promise<string | null>;
  updateThumbnailStatus: (
    canvasId: string,
    size: 'small' | 'big',
    status: 'pending' | 'loaded' | 'error',
    url: string,
  ) => void;
  queueThumbnailGeneration: (
    canvasId: string,
    size: 'small' | 'big',
    forceRegenerate?: boolean,
    priority?: number,
    useInitialDataOnly?: boolean,
  ) => void;
  processNextThumbnail: () => Promise<void>;
  // method for checking the update status
  isPendingUpdateCanvas: (canvasId: string) => boolean;
  // methods for blocking/unblocking thumbnail processing
  blockThumbnailProcessing: () => void;
  unblockThumbnailProcessing: () => void;
  // update only initialData for canvas
  updateInitialData: (canvasId: string, initialData: any) => void;
  // установить marker_type для canvas
  setMarkerType: (canvasId: string, markerType: string) => void;
  // получить marker_type для canvas
  getMarkerType: (canvasId: string) => string | undefined;
  // методы для управления блокировкой канваса
  lockCanvas: (canvasId: string, reason?: string) => void;
  unlockCanvas: (canvasId: string) => void;
  isCanvasLocked: (canvasId: string) => boolean;
  // методы для работы с глобальной историей
  addToGlobalHistory: (action: GlobalHistoryAction) => void;
  globalUndo: () => Promise<void>;
  globalRedo: () => Promise<void>;
  canGlobalUndo: () => boolean;
  canGlobalRedo: () => boolean;
  clearGlobalHistory: () => void;
  // методы для работы со стилевыми границами
  detectUndoContext: () => 'normal' | 'style_rollback' | 'style_forward';
  undoToStyleBoundary: (boundaryType: 'style_changed' | 'restyling_done') => Promise<void>;
  redoToStyleBoundary: (boundaryType: 'style_changed' | 'restyling_done') => Promise<void>;
  findLastBackupBeforeBoundary: (slideId: string, boundaryIndex: number) => any | null;
  restoreSlideToBackup: (slideId: string, backupData: any) => Promise<void>;
  // методы для работы с персистентностью
  saveWorkspaceToStorage: (workspaceId: string) => void;
  loadWorkspaceFromStorage: (workspaceId: string) => Promise<boolean>;
  clearWorkspaceStorage: (workspaceId: string) => void; // Удаляет весь canvas-workspace-storage
  cleanupOldWorkspaces: (maxAge?: number) => void;
};

type CanvasStore = CanvasActions & CanvasStoreState;

const defaultState: CanvasStoreState = {
  saveBackupTimers: new Map(),
  thumbnailTimers: new Map(),
  thumbnailQueue: [],
  isProcessingThumbnail: false,
  isPendingUpdate: false,
  activeCanvasId: null,
  canvasData: {},
  buffer: {
    objects: [],
    sourceCanvasId: null,
  },
  isUndoOrRedoInProgress: false,
  workspaceId: '',
  globalHistory: {
    actions: [],
    currentActionIndex: -1,
  },
};

const delayForSaveBackupToServer = 2000;

export const useCanvasStore = create<CanvasStore>((set, get) => ({
  ...defaultState,

  setIsNeedUpdateFabricInstance: (canvasId, isNeedUpdate) => {
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        [canvasId]: { ...state.canvasData[canvasId], isNeedUpdateFabricInstance: isNeedUpdate },
      },
    }));
  },

  saveBackupToServer: (canvasId, state) => {
    const { saveBackupTimers } = get();

    // Set isPendingUpdate in true, showing that the slide is waiting for update
    set((currentState) => ({
      canvasData: {
        ...currentState.canvasData,
        [canvasId]: {
          ...currentState.canvasData[canvasId],
          isPendingUpdate: true, // Set flag in true before sending
        },
      },
    }));

    if (saveBackupTimers.has(canvasId)) {
      clearTimeout(saveBackupTimers.get(canvasId));
    }

    const timer = setTimeout(async () => {
      try {
        await retryFetch({
          cb: async () => {
            // TODO: fix url
            const url = serverPath.workspace.saveSlideConfig(get().workspaceId, canvasId);
            set((currentState) => ({
              canvasData: {
                ...currentState.canvasData,
                [canvasId]: {
                  ...currentState.canvasData[canvasId],
                  savedLastVersionStatus: {
                    isSaved: false,
                    isSaving: true,
                    error: null,
                  },
                },
              },
            }));
            // console.debug(`🔄 Saving backup for canvas: ${canvasId}`, state);
            return fetch(url, {
              method: 'POST',
              headers: prepareHeaders(),
              body: JSON.stringify(state),
            })
              .then((res) => {
                if (!res.ok)
                  throw new FetchError(`HTTP error! status: ${res.status}`, res.url, res.status);
                return res.json();
              })
              .catch((err: FetchError) => {
                console.error('Error saving backup:', err);
                handleFetchError(err, get().workspaceId);
                throw err;
              });
          },
          retries: 3,
          delayBetween: 550,
        });

        //Analytics. Send event if more 3 backups
        if (get().workspaceId !== undefined && get().canvasData[canvasId]?.backups?.length > 3) {
          const flow = getFlowFromUrl(window.location.search);
          analytics.emitEvent('design_editing_canvas', {
            [PlatformName.GTM]: {
              processed_file_id: get().workspaceId,
              flow,
            },
          });
        }

        set((currentState) => ({
          canvasData: {
            ...currentState.canvasData,
            [canvasId]: {
              ...currentState.canvasData[canvasId],
              savedLastVersionStatus: {
                isSaved: true,
                isSaving: false,
                error: null,
              },
              isPendingUpdate: false, // Reset flag after successful update
            },
          },
        }));
      } catch (error) {
        console.error(`⛔ Error saving backup for canvas ${canvasId}:`, error);

        Sentry.captureException(error, {
          tags: { feature: 'backup_save', canvasId },
          extra: {
            workspaceId: get().workspaceId,
            canvasId,
            retries: 3,
          },
        });

        set((currentState) => ({
          canvasData: {
            ...currentState.canvasData,
            [canvasId]: {
              ...currentState.canvasData[canvasId],
              savedLastVersionStatus: {
                isSaved: false,
                isSaving: false,
                error: 'Error sync',
              },
              isPendingUpdate: false, // Reset flag even if there is an error
            },
          },
        }));
      }
      get().saveBackupTimers.delete(canvasId);
    }, delayForSaveBackupToServer);

    set((state) => {
      const newTimers = new Map(state.saveBackupTimers);
      newTimers.set(canvasId, timer);
      return { saveBackupTimers: newTimers };
    });
  },

  isPendingUpdateCanvas: (canvasId) => {
    const canvas = get().canvasData[canvasId];
    if (!canvas) return false;

    // The slide is waiting for update if:
    // 1. The flag isPendingUpdate is set
    // 2. Or the saving process is in progress (isSaving = true)
    return canvas.isPendingUpdate || canvas.savedLastVersionStatus.isSaving;
  },

  setActiveCanvasId: (canvasId) => {
    const currentActiveCanvasId = get().activeCanvasId;

    // Записываем переключение слайда в глобальную историю только если это действительно переключение
    if (currentActiveCanvasId !== canvasId && currentActiveCanvasId !== null) {
      get().addToGlobalHistory({
        type: 'slide_switch',
        slideId: canvasId,
        timestamp: Date.now(),
      });
    }

    set({ activeCanvasId: canvasId });
  },

  setWorkspaceId: (workspaceId) => {
    set({ workspaceId });
  },

  removeCanvas: (canvasId) => {
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [canvasId]: _, ...remainingCanvasData } = state.canvasData;
      return { canvasData: remainingCanvasData };
    });
    if (get().activeCanvasId === canvasId) {
      set({ activeCanvasId: null });
    }
  },

  addCanvas: ({
    canvasId,
    initialData,
    fabricInstance,
    element,
    backupDepth = 1500,
    metaData = {},
    canvasScale = CANVAS_SCALE,
    canvasDimensions = CANVAS_DIMENSIONS,
    isRegistered = true,
    position,
  }: {
    canvasId: string;
    initialData: any;
    fabricInstance: Canvas | null;
    element: HTMLElement | null;
    backupDepth?: number;
    metaData?: MetaData;
    canvasScale?: number;
    canvasDimensions?: { width: number; height: number };
    isRegistered?: boolean;
    position?: number;
  }) => {
    const { loggingEnabled } = useLoggingStore.getState();
    if (loggingEnabled['CanvasWidget.addCanvas']) {
      // console.debug(`✅ Canvas added: ${canvasId}`);
      // console.debug(`✅ Canvas added: ${canvasId}`);
    }
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        [canvasId]: {
          initialData,
          fabricInstance,
          element,
          backups: [...(state.canvasData[canvasId]?.backups || [])],
          currentStateIndex: state.canvasData[canvasId]?.currentStateIndex || 0,
          backupDepth,
          layersMap: {
            // Init layersMap for every canvas
            background: [],
            middle: [],
            foreground: [],
            overlay: [],
          } as LayersMap,
          metaData,
          canvasScale,
          canvasDimensions,
          savedLastVersionStatus: {
            isSaved: true,
            isSaving: false,
            error: null,
          },
          isPendingUpdate: false, // Initialize isPendingUpdate flag
          // Initialize movement state
          movementState: {
            lastDirection: null,
            hasPendingChanges: false,
            activeObjects: [],
          },
          thumbnail: {
            smallImg: {
              status: state.canvasData[canvasId]?.thumbnail?.smallImg?.status || 'pending',
              url: state.canvasData[canvasId]?.thumbnail?.smallImg?.url || '',
            },
            bigImg: {
              status: state.canvasData[canvasId]?.thumbnail?.bigImg?.status || 'pending',
              url: state.canvasData[canvasId]?.thumbnail?.bigImg?.url || '',
            },
          },
          isNeedUpdateFabricInstance: false,
          isNeedUpdateElement: false,
          isRelayout: false,
          isRegistered,
          isRepaint: false,
          position,
          // Извлекаем marker_type из initialData.extra.marker_type при создании canvas
          markerType: initialData?.extra?.marker_type || 'normal',
        },
      },
    }));

    // DON'T SET ACTIVE CANVAS ID HERE!
    // if (!get().activeCanvasId) {
    //   console.debug('set activeCanvasId ->', canvasId);
    //   get().setActiveCanvasId(canvasId);
    // }
  },

  updateFabricInstance: (canvasId, fabricInstance) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: { ...canvas, fabricInstance, isNeedUpdateFabricInstance: false },
        },
      };
    });
  },

  updateCanvasElement: (canvasId, canvasElement) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: { ...canvas, element: canvasElement, isNeedUpdateElement: false },
        },
      };
    });
  },

  updateMetaData: (canvasId, meta: MetaData) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            metaData: { ...canvas.metaData, ...meta },
          },
        },
      };
    });
  },

  getMetaData: (canvasId) => {
    return get().canvasData[canvasId]?.metaData || null;
  },

  updateInitialData: (canvasId, initialData) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      // Извлекаем marker_type из initialData.extra.marker_type
      const markerType = initialData?.extra?.marker_type || 'normal';

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            initialData,
            markerType,
          },
        },
      };
    });
  },

  setMarkerType: (canvasId, markerType) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            markerType,
          },
        },
      };
    });
  },

  getMarkerType: (canvasId) => {
    const canvas = get().canvasData[canvasId];
    if (!canvas) return undefined;

    // Сначала проверяем сохраненный markerType
    if (canvas.markerType) {
      return canvas.markerType;
    }

    // Fallback на initialData.extra.marker_type
    const markerType = canvas.initialData?.extra?.marker_type;
    return markerType || 'normal';
  },

  lockCanvas: (canvasId, reason) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            isLocked: true,
            lockReason: reason,
          },
        },
      };
    });
  },

  unlockCanvas: (canvasId) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            isLocked: false,
            lockReason: undefined,
          },
        },
      };
    });
  },

  isCanvasLocked: (canvasId) => {
    const canvas = get().canvasData[canvasId];
    return canvas?.isLocked === true;
  },

  deleteCanvas: (canvasId) => {
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [canvasId]: _, ...remainingCanvasData } = state.canvasData;
      return { canvasData: remainingCanvasData };
    });

    if (get().activeCanvasId === canvasId) {
      set({ activeCanvasId: null });
    }
  },

  getCanvasState: (canvasId) => {
    return get().canvasData[canvasId] || null;
  },

  getFabricInstance: (canvasId) => {
    return get().canvasData[canvasId]?.fabricInstance || null;
  },

  addBackup: (canvasId, state, sendToServer = true, generateThumbnail = true) => {
    let shouldGenerateThumbnail = false;
    let newBackupIndex = -1;

    set((currentState) => {
      const { loggingEnabled } = useLoggingStore.getState();

      const canvas = currentState.canvasData[canvasId];
      if (!canvas) return currentState;

      // === ПРОВЕРКА БЛОКИРОВКИ КАНВАСА ===
      const fabricInstance = canvas.fabricInstance;
      if (fabricInstance && isCanvasLocked(fabricInstance)) {
        const lockInfo = fabricInstance._lockState;
        console.warn(
          `🚫 Blocking addBackup: canvas is locked (${lockInfo?.lockReason || 'no reason'})`,
        );
        return currentState; // Не создаем бекап если канвас заблокирован
      }

      // Check for snaplines and clean
      const cleanState = cleanStateFromSnaplines(state);
      // Restore original opacity values before saving to backup
      const stateWithOriginalOpacities = restoreOriginalOpacitiesInState(cleanState);

      const truncatedBackups = canvas.backups.slice(0, canvas.currentStateIndex + 1);

      if (truncatedBackups.length > 0) {
        const lastBackup = truncatedBackups.at(-1);
        if (lastBackup.isChangedContent) {
          stateWithOriginalOpacities.isChangedContent = true;
        }
      }

      const newBackups = [...truncatedBackups, stateWithOriginalOpacities];
      const limitedBackups = newBackups.slice(-canvas.backupDepth);

      // Сохраняем индекс нового бэкапа для глобальной истории
      newBackupIndex = limitedBackups.length - 1;

      // Определяем нужно ли генерировать тумбнейл на основе newBackups
      shouldGenerateThumbnail = generateThumbnail && limitedBackups.length > 0;

      if (loggingEnabled['addBackup']) {
        // console.debug(`➕🗳️ Backups added for: ${canvasId}:`, cleanState);
      }

      return {
        canvasData: {
          ...currentState.canvasData,
          [canvasId]: {
            ...canvas,
            backups: limitedBackups,
            currentStateIndex: limitedBackups.length - 1,
            savedLastVersionStatus: {
              isSaved: true,
              isSaving: false,
              error: null,
            },
            // If data will be sent to server, set the flag of waiting
            isPendingUpdate: sendToServer ? true : canvas.isPendingUpdate,
          },
        },
      };
    });

    // Добавляем действие в глобальную историю
    if (newBackupIndex >= 0) {
      get().addToGlobalHistory({
        type: 'slide_backup',
        slideId: canvasId,
        backupIndex: newBackupIndex,
        timestamp: Date.now(),
      });
    }

    // Save backup to server
    if (sendToServer) {
      get().saveBackupToServer(canvasId, state);
    }

    // Автосохранение workspace state при каждом 5-м бэкапе для производительности
    const shouldAutoSave = newBackupIndex >= 0 && newBackupIndex % 5 === 0;
    if (shouldAutoSave && get().workspaceId) {
      setTimeout(() => {
        try {
          get().saveWorkspaceToStorage(get().workspaceId);
        } catch (error) {
          console.error('❌ [Persist] Auto-save failed during backup:', error);
        }
      }, 1000); // Небольшая задержка чтобы не блокировать UI
    }

    // Generate thumbnail if needed
    if (shouldGenerateThumbnail) {
      // Cancel previous timer generation, if it exists
      const thumbnailTimerId = `thumbnail_${canvasId}`;
      if (get().thumbnailTimers.has(thumbnailTimerId)) {
        const oldTimer = get().thumbnailTimers.get(thumbnailTimerId);

        clearTimeout(oldTimer);
        get().thumbnailTimers.delete(thumbnailTimerId);
      }

      // Create a new timer with debounce
      const timer = setTimeout(() => {
        // Проверяем что таймер все еще актуален
        const currentTimer = get().thumbnailTimers.get(thumbnailTimerId);
        if (currentTimer !== timer) {
          return;
        }

        // Add task to the queue with high priority (10), since this is an active slide
        get().queueThumbnailGeneration(canvasId, 'small', true, 10);

        // After the small thumbnail is generated, create a large one with a lower priority
        setTimeout(() => {
          get().queueThumbnailGeneration(canvasId, 'big', true, 5);
        }, 100);

        get().thumbnailTimers.delete(thumbnailTimerId);
      }, 500); // Debounce in 0.5 seconds

      // Save the timer to the new map
      set((state) => {
        const newTimers = new Map(state.thumbnailTimers);
        newTimers.set(thumbnailTimerId, timer);

        return { thumbnailTimers: newTimers };
      });
    }
  },

  undo: async (canvasId, skipGlobalCheck = false) => {
    const { loggingEnabled } = useLoggingStore.getState();
    const canvas = get().canvasData[canvasId];
    if (
      !canvas ||
      canvas.currentStateIndex === 0 ||
      (!skipGlobalCheck && get().isUndoOrRedoInProgress)
    )
      return null;

    if (!skipGlobalCheck) {
      set({ isUndoOrRedoInProgress: true });
    }

    const newIndex = canvas.currentStateIndex - 1;
    const lastState = canvas.backups[newIndex];

    set((currentState) => ({
      canvasData: {
        ...currentState.canvasData,
        [canvasId]: {
          ...canvas,
          currentStateIndex: newIndex,
          savedLastVersionStatus: {
            // Mark that the latest version is not saved to backend
            isSaved: false,
            isSaving: true,
            error: null,
          },
        },
      },
    }));

    const fabricInstance = canvas.fabricInstance;
    if (fabricInstance) {
      if (loggingEnabled['CanvasWidget.undo']) {
        // console.debug(`🔄 Undo loading state:`, lastState);
      }
      try {
        await loadObjectsWithLayers(fabricInstance, canvasId, lastState);
        fabricInstance.renderAll();
        if (loggingEnabled['CanvasWidget.undo']) {
          // console.debug(`👈 Undo state loaded:`, lastState);
        }
        await wait(150);

        // МОДИФИКАЦИЯ: Используем новую систему очереди с высоким приоритетом (8)
        get().queueThumbnailGeneration(canvasId, 'small', true, 8);
        get().queueThumbnailGeneration(canvasId, 'big', true, 3);
      } catch (err) {
        if (loggingEnabled['CanvasWidget.undo']) {
          console.error(`⛔ Error while loading state:`, err);
        }
      }
    }

    get().saveBackupToServer(canvasId, lastState);

    if (!skipGlobalCheck) {
      set({ isUndoOrRedoInProgress: false });
    }

    return lastState;
  },

  redo: async (canvasId, skipGlobalCheck = false) => {
    const { loggingEnabled } = useLoggingStore.getState();
    const canvas = get().canvasData[canvasId];
    if (
      !canvas ||
      canvas.currentStateIndex >= canvas.backups.length - 1 ||
      (!skipGlobalCheck && get().isUndoOrRedoInProgress)
    ) {
      return null;
    }

    if (!skipGlobalCheck) {
      set({ isUndoOrRedoInProgress: true });
    }

    const newIndex = canvas.currentStateIndex + 1;
    const nextState = canvas.backups[newIndex];

    set((currentState) => ({
      canvasData: {
        ...currentState.canvasData,
        [canvasId]: {
          ...canvas,
          currentStateIndex: newIndex,
          savedLastVersionStatus: {
            isSaved: false,
            isSaving: true,
            error: null,
          },
        },
      },
    }));

    const fabricInstance = canvas.fabricInstance;
    if (fabricInstance) {
      if (loggingEnabled['redo']) {
        // console.debug(`🔄 Undo loading state:`, nextState);
      }

      try {
        await loadObjectsWithLayers(fabricInstance, canvasId, nextState);
        fabricInstance.renderAll();
        if (loggingEnabled['redo']) {
          // console.debug('👉 Redo:', nextState);
        }
        await wait(150);

        // МОДИФИКАЦИЯ: Используем новую систему очереди с высоким приоритетом (8)
        get().queueThumbnailGeneration(canvasId, 'small', true, 8);
        get().queueThumbnailGeneration(canvasId, 'big', true, 3);
      } catch (err) {
        if (loggingEnabled['redo']) {
          console.error('⛔ Error (Redo):', err);
        }
      }
    }

    get().saveBackupToServer(canvasId, nextState);

    if (!skipGlobalCheck) {
      set({ isUndoOrRedoInProgress: false });
    }

    return nextState;
  },

  closeSelectionFrame: () => {
    const { canvasData, activeCanvasId } = get();
    if (!activeCanvasId) {
      console.warn('⛔ Active canvas not found.');
      return;
    }
    const canvasState = canvasData[activeCanvasId];
    if (!canvasState) {
      console.warn('⛔ Canvas not found.');
      return;
    }
    const fabricInstance = canvasState.fabricInstance;
    if (!fabricInstance) {
      console.warn('⛔ Fabric instance not found.');
      return;
    }
    fabricInstance.discardActiveObject();
    fabricInstance.renderAll();
  },

  updateCanvasScale: (canvasId, scale) => {
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        [canvasId]: {
          ...state.canvasData[canvasId],
          canvasScale: scale,
        },
      },
    }));
  },

  updateCanvasDimensions: (canvasId, dimensions, scale) => {
    set((state) => ({
      canvasData: {
        ...state.canvasData,
        [canvasId]: {
          ...state.canvasData[canvasId],
          canvasDimensions: dimensions,
          canvasScale: scale ?? state.canvasData[canvasId].canvasScale,
        },
      },
    }));
  },

  checkActiveObjectsChanged: (canvasId) => {
    const { canvasData } = get();
    const canvas = canvasData[canvasId];
    if (!canvas || canvas.movementState.activeObjects.length === 0) {
      return false;
    }

    const fabricInstance = canvas.fabricInstance;
    if (!fabricInstance) {
      return false;
    }

    // Get current active objects
    const currentActiveObjects = fabricInstance
      .getActiveObjects()
      .map((obj) => obj.id || '')
      .filter((id) => id);

    // Check if there's a difference in the set of active objects
    if (currentActiveObjects.length !== canvas.movementState.activeObjects.length) {
      return true;
    }

    // Check if all previously active objects are still active
    return !canvas.movementState.activeObjects.every((id) => currentActiveObjects.includes(id));
  },

  // layers
  addLayerObject: (canvasId, layer, object) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      const objId = object.object.id;
      // const layerCountBefore = canvas.layersMap[layer].length;
      const existingInLayer = canvas.layersMap[layer].find((item) => item.object.id === objId);

      if (existingInLayer) {
        // console.warn('⚠️ [store.addLayerObject] Объект уже в слое, пропускаем:', {
        //   canvasId,
        //   layer,
        //   objId,
        //   objType: object.object.type,
        //   layerCountBefore,
        // });
        return state;
      }

      // console.log('📝 [store.addLayerObject] Добавляем объект в store:', {
      //   canvasId,
      //   layer,
      //   objId,
      //   objType: object.object.type,
      //   layerCountBefore,
      //   layerCountAfter: layerCountBefore + 1,
      // });

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            layersMap: {
              ...canvas.layersMap,
              [layer]: [...canvas.layersMap[layer], object],
            },
          },
        },
      };
    });
  },

  removeLayerObject: (canvasId, layer, object) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            layersMap: {
              ...canvas.layersMap,
              [layer]: canvas.layersMap[layer].filter(
                (item) => item.object.id !== object.object.id,
              ),
            },
          },
        },
      };
    });
  },

  clearLayer: (canvasId, layer) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state; // Check if the canvas exists

      const layerCountBefore = canvas.layersMap[layer].length;
      console.log('🗑️ [store.clearLayer] Очищаем слой в store:', {
        canvasId,
        layer,
        layerCountBefore,
      });

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            layersMap: {
              ...canvas.layersMap,
              [layer]: [],
            },
          },
        },
      };
    });
  },

  // copying and pasting
  copyToBuffer: async (canvasId: string) => {
    const canvasState = get().canvasData[canvasId];
    if (!canvasState) {
      console.warn('⛔ Canvas not found.');
      return;
    }

    const fabricInstance = canvasState.fabricInstance;
    if (!fabricInstance) {
      console.warn('⛔ Fabric instance not found.');
      return;
    }

    // console.debug('🧹 Clearing buffer before copying:', get().buffer);
    set({
      buffer: {
        objects: [],
        sourceCanvasId: canvasId,
      },
    });
    // console.debug('📭 Buffer cleared:', get().buffer);

    try {
      const copiedObjects = await copyObjects(fabricInstance, canvasId);

      set({
        buffer: {
          objects: copiedObjects,
          sourceCanvasId: canvasId,
        },
      });
      // console.debug('📬 Objects copied and pasted to buffer:', copiedObjects);
    } catch (error) {
      console.error('⛔ Error copying objects:', error);
    }
  },

  pasteFromBuffer: async (
    targetCanvasId: string,
    options?: {
      insertCoordinates?: { left: number; top: number };
      offsetX?: number;
      offsetY?: number;
      isNeedSetActivePastedObject?: boolean; // Activate pasted objects after pasting (true by default)
    },
  ) => {
    const { buffer, canvasData } = get();
    const canvasState = canvasData[targetCanvasId];
    if (!canvasState) {
      console.warn('⛔ Target canvas not found.');
      return;
    }

    const fabricInstance = canvasState.fabricInstance;
    if (!fabricInstance) {
      console.warn('⛔ Fabric instance not found.');
      return;
    }

    if (buffer.objects.length === 0) {
      console.warn('❓ Buffer is empty. Nothing to paste.');
      return;
    }

    try {
      await pasteObjects(fabricInstance, buffer.objects, targetCanvasId, options);
      // console.debug('📥 Objects pasted from buffer to canvas:', targetCanvasId);
    } catch (error) {
      console.error('⛔ Error pasting objects:', error);
    }
  },

  // methods for object movement management
  updateMovementState: (canvasId, objects, direction) => {
    const canvas = get().canvasData[canvasId];
    if (!canvas) return false;

    const previousDirection = canvas.movementState.lastDirection;

    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      // Store active object IDs
      const activeObjectIds = objects.map((obj) => obj.id || '').filter((id) => id);

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            movementState: {
              lastDirection: direction,
              hasPendingChanges: true,
              activeObjects: activeObjectIds,
            },
          },
        },
      };
    });

    // Check if the movement direction has changed
    return previousDirection !== null && previousDirection !== direction;
  },

  finishObjectMovement: (canvasId, forceBackup = false) => {
    const { canvasData } = get();
    const canvas = canvasData[canvasId];
    if (!canvas || !canvas.movementState.hasPendingChanges) {
      return false;
    }

    if (forceBackup) {
      // Reset state
      set((state) => {
        const canvas = state.canvasData[canvasId];
        if (!canvas) return state;

        return {
          canvasData: {
            ...state.canvasData,
            [canvasId]: {
              ...canvas,
              movementState: {
                lastDirection: null,
                hasPendingChanges: false,
                activeObjects: [],
              },
            },
          },
        };
      });

      // Create a backup
      const fabricInstance = canvas.fabricInstance;
      if (fabricInstance) {
        get().addBackup(canvasId, fabricInstance.toJSON());
      }

      return true;
    }

    return false;
  },

  // relayout
  setIsRelayout: (canvasId, isRelayout) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return { canvasData: { ...state.canvasData, [canvasId]: { ...canvas, isRelayout } } };
    });
  },

  setIsRepaint: (canvasId, isRepaint) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;
      return { canvasData: { ...state.canvasData, [canvasId]: { ...canvas, isRepaint } } };
    });
  },

  // thumbnails
  generateThumbnail: async (
    canvasId,
    size,
    forceRegenerate = false,
    useInitialDataOnly = false,
    retryAttempt = 0,
  ) => {
    const canvasState = get().canvasData[canvasId];
    if (!canvasState) return null;

    // console.debug('🖼️ [generateThumbnail] Starting:', {
    //   canvasId,
    //   size,
    //   forceRegenerate,
    //   useInitialDataOnly,
    //   hasBackups: canvasState.backups.length > 0,
    //   currentStateIndex: canvasState.currentStateIndex,
    //   hasInitialData: !!canvasState.initialData,
    //   initialDataPreview: canvasState.initialData
    //     ? Object.keys(canvasState.initialData).slice(0, 5)
    //     : null,
    // });

    // Проверка существующей миниатюры
    if (
      !forceRegenerate &&
      canvasState.thumbnail?.[size === 'small' ? 'smallImg' : 'bigImg']?.status === 'loaded' &&
      canvasState.thumbnail?.[size === 'small' ? 'smallImg' : 'bigImg']?.url
    ) {
      // console.debug('🖼️ [generateThumbnail] Using existing thumbnail:', { canvasId, size });
      return canvasState.thumbnail[size === 'small' ? 'smallImg' : 'bigImg'].url;
    }

    // Получаем текущий статус миниатюры
    const currentThumbnailData = canvasState.thumbnail?.[size === 'small' ? 'smallImg' : 'bigImg'];
    const hasExistingThumbnail =
      currentThumbnailData?.url && currentThumbnailData?.status === 'loaded';

    // Устанавливаем статус "ожидание" только если нет существующей миниатюры
    if (!hasExistingThumbnail) {
      get().updateThumbnailStatus(canvasId, size, 'pending', '');
    }

    const slideMetadata = extractSlideMetadata(canvasState.initialData);

    // ИСПРАВЛЕНИЕ: Создаем реальный DOM элемент для StaticCanvas
    // Проблема: StaticCanvas(undefined, {...}) создает canvas без DOM элемента,
    // что приводит к ошибке "Cannot read properties of undefined (reading 'ctx')"
    // при вызове методов clear(), renderAll() и т.д.
    let tempCanvasElement: HTMLCanvasElement | null = null;
    let tempCanvas: StaticCanvas | null = null;

    try {
      // Создаем временный canvas элемент в DOM
      tempCanvasElement = document.createElement('canvas');
      tempCanvasElement.style.display = 'none';
      tempCanvasElement.style.position = 'absolute';
      tempCanvasElement.style.left = '-9999px';
      document.body.appendChild(tempCanvasElement);

      const canvasWidth =
        canvasState.metaData?.slide_width ?? slideMetadata?.width ?? CANVAS_DIMENSIONS.width;
      const canvasHeight =
        canvasState.metaData?.slide_height ?? slideMetadata?.height ?? CANVAS_DIMENSIONS.height;

      // ВАЛИДАЦИЯ: Проверяем валидность размеров
      if (!canvasWidth || !canvasHeight || canvasWidth <= 0 || canvasHeight <= 0) {
        throw new Error(`Invalid canvas dimensions: width=${canvasWidth}, height=${canvasHeight}`);
      }

      // Создаем StaticCanvas с реальным DOM элементом
      tempCanvas = new StaticCanvas(tempCanvasElement, {
        width: canvasWidth,
        height: canvasHeight,
      });

      //  ЗАЩИТА: Проверяем что canvas инициализирован корректно
      if (!tempCanvas || typeof tempCanvas.toDataURL !== 'function') {
        console.error('❌ [generateThumbnail] Failed to initialize temp canvas:', { canvasId });
        throw new Error('Failed to initialize temporary canvas - toDataURL not available');
      }

      //  ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Убеждаемся что контекст доступен
      if (!tempCanvasElement || !tempCanvasElement.getContext) {
        throw new Error('Failed to get canvas element context');
      }
      const context = tempCanvasElement.getContext('2d');
      if (!context) {
        throw new Error('Failed to get canvas context - canvas may be disposed');
      }

      // КЛЮЧЕВАЯ ЛОГИКА ВЫБОРА ИСТОЧНИКА ДАННЫХ ДЛЯ ГЕНЕРАЦИИ ТУМБОВ
      //
      // ПРОБЛЕМА ПОСЛЕ РЕСТАЙЛИНГА:
      // - localStorage содержит старые backup данные с прежним дизайном
      // - По умолчанию generateThumbnail использует backup данные (если есть)
      // - Результат: тумбы генерируются по старому дизайну
      //
      // РЕШЕНИЕ:
      // - Добавили флаг useInitialDataOnly для принудительного использования initialData
      // - initialData обновляется в Thumbnail компоненте когда приходит новый slide.config
      // - Флаг позволяет обойти backup данные и использовать новый дизайн
      const latestBackup = canvasState.backups[canvasState.currentStateIndex];
      const shouldUseInitialData = useInitialDataOnly || !latestBackup;

      // console.debug('🖼️ [generateThumbnail] Data source decision:', {
      //   canvasId,
      //   useInitialDataOnly,
      //   hasLatestBackup: !!latestBackup,
      //   backupsLength: canvasState.backups.length,
      //   currentStateIndex: canvasState.currentStateIndex,
      //   shouldUseInitialData,
      //   reason: useInitialDataOnly
      //     ? 'forced by flag'
      //     : !latestBackup
      //       ? 'no backup'
      //       : 'using backup',
      // });

      if (shouldUseInitialData) {
        // ✅ ИСПОЛЬЗУЕМ INITIALDATA (новый дизайн после рестайлинга)
        const initialData = canvasState.initialData;
        if (!initialData || !initialData.objects) {
          throw new Error(
            'No data for thumbnail generation - neither backup nor initialData available',
          );
        }

        // ВАЛИДАЦИЯ: Проверяем что есть объекты для отрисовки
        if (!Array.isArray(initialData.objects) || initialData.objects.length === 0) {
          console.warn(
            `⚠️ [generateThumbnail] InitialData has no objects for canvas ${canvasId}, generating empty thumbnail`,
          );
        }

        // console.debug('🖼️ [generateThumbnail] Using initialData:', {
        //   canvasId,
        //   reason: useInitialDataOnly ? 'useInitialDataOnly=true' : 'no backup available',
        //   objectsCount: initialData.objects?.length || 0,
        // });

        // Загружаем данные из initialData в временный канвас
        await loadObjectsWithLayers(tempCanvas, canvasId, initialData, false);

        // Отключаем objectCaching для снижения пикового числа canvas-контекстов.
        // Каждый закэшированный объект создаёт внутренний _cacheCanvas;
        // при наличии clipPath Fabric дополнительно создаёт canvas в createClipPathLayer.
        // Для одноразового рендера тумбнейла кэширование не нужно.
        tempCanvas.getObjects().forEach((obj) => {
          (obj as any).objectCaching = false;
        });

        // ✅ ПРОВЕРКА: Убеждаемся что canvas не был disposed во время загрузки
        if (!tempCanvas || !tempCanvasElement || !tempCanvasElement.getContext('2d')) {
          throw new Error('Canvas was disposed during object loading');
        }

        tempCanvas.renderAll();
        await new Promise((r) => setTimeout(r, 200));
      } else {
        // console.debug('🖼️ [generateThumbnail] Using backup data:', {
        //   canvasId,
        //   backupIndex: canvasState.currentStateIndex,
        //   backupObjectsCount: latestBackup.objects?.length || 0,
        // });

        // ИСПОЛЬЗУЕМ BACKUP ДАННЫЕ (обычный режим, не после рестайлинга)

        // ВАЛИДАЦИЯ: Проверяем что backup данные валидны
        if (!latestBackup || typeof latestBackup !== 'object') {
          throw new Error('Invalid backup data structure');
        }

        if (!tempCanvas) {
          throw new Error('Temp canvas is null');
        }

        // Fabric.js v6: loadFromJSON возвращает Promise и внутренне вызывает this.clear().
        // Второй аргумент - reviver (не completion callback как в v5).
        // Необходимо await'ить возвращённый Promise, иначе this.clear() внутри
        // .then() вызовется после dispose() в finally блоке.
        await tempCanvas.loadFromJSON(latestBackup);

        // Отключаем objectCaching для снижения пикового числа canvas-контекстов.
        // Каждый закэшированный объект создаёт внутренний _cacheCanvas;
        // при наличии clipPath Fabric дополнительно создаёт canvas в createClipPathLayer.
        // Для одноразового рендера тумбнейла кэширование не нужно.
        tempCanvas.getObjects().forEach((obj) => {
          (obj as any).objectCaching = false;
        });

        // Проверяем что canvas не был disposed во время загрузки
        if (!tempCanvas || !tempCanvasElement || !tempCanvasElement.getContext('2d')) {
          throw new Error('Canvas was disposed during backup loading');
        }

        // loadFromJSON восстанавливает backgroundImage из сериализованных данных.
        // Проверяем есть ли backgroundImage на верхнем уровне backup (от canvas.toJSON())
        const backupBgImage = latestBackup.backgroundImage;
        let bgImageUrl: string | null = null;

        // Определяем URL фонового изображения из разных источников
        if (backupBgImage) {
          // backgroundImage из canvas.toJSON() - это объект с src
          if (typeof backupBgImage === 'object' && backupBgImage.src) {
            bgImageUrl = backupBgImage.src;
          } else if (typeof backupBgImage === 'string') {
            bgImageUrl = backupBgImage;
          }
        }

        // Если нет на верхнем уровне - ищем в объекте category: 'background'
        if (!bgImageUrl) {
          const backgroundObject = findBackgroundObject(tempCanvas as Canvas);
          if (backgroundObject) {
            const bgColor = (backgroundObject as any).backgroundColor;
            const bgImage = (backgroundObject as any).backgroundImage;

            if (bgColor) {
              (tempCanvas as any).backgroundColor = bgColor;
            }

            if (bgImage) {
              bgImageUrl = bgImage;
            }

            if (!bgColor && !bgImage) {
              (tempCanvas as any).backgroundColor = 'white';
            }
          } else {
            (tempCanvas as any).backgroundColor = 'white';
          }
        }

        // Если есть URL фонового изображения - загружаем и ждем полной загрузки
        if (bgImageUrl) {
          try {
            await setCanvasBackgroundImage(
              tempCanvas,
              bgImageUrl,
              tempCanvas.width,
              tempCanvas.height,
            );
          } catch (error) {
            console.warn('[generateThumbnail] Failed to set background image from backup:', error);
          }
        }

        tempCanvas.renderAll();
        // Небольшая задержка для стабильности рендера
        await new Promise((r) => setTimeout(r, 100));

        // Финальная проверка: убеждаемся что canvas все еще валиден
        if (!tempCanvas || !tempCanvasElement || !tempCanvasElement.getContext('2d')) {
          throw new Error('Canvas was disposed after rendering');
        }
      }

      // Генерируем миниатюру с нужным качеством
      const multiplier = size === 'small' ? 0.5 : 2;
      const quality = size === 'small' ? 0.7 : 1;

      try {
        // Проверка размеров канваса
        if (tempCanvas.width < 10 || tempCanvas.height < 10) {
          console.warn('Canvas too small', tempCanvas.width, tempCanvas.height);
          tempCanvas.setDimensions({
            width: Math.max(tempCanvas.width, CANVAS_DIMENSIONS.width),
            height: Math.max(tempCanvas.height, CANVAS_DIMENSIONS.height),
          });
          tempCanvas.requestRenderAll();
          await new Promise((r) => setTimeout(r, 150));
        }

        // Еще одна пауза перед генерацией URL
        await new Promise((r) => setTimeout(r, 150));

        const dataURL = tempCanvas.toDataURL({
          format: 'png',
          quality: quality,
          multiplier: multiplier,
          enableRetinaScaling: size === 'big',
        });

        // Проверка валидности dataURL
        if (!dataURL || dataURL === 'data:,') {
          throw new Error('Received dataURL is empty or corrupted');
        }

        //  ПРОВЕРКА НА ПУСТУЮ МИНИАТЮРУ: Предотвращаем сохранение пустых миниатюр
        if (checkIsEmptyThumbnailBase64(dataURL)) {
          console.warn(
            `⚠️ [generateThumbnail] Generated empty thumbnail for canvas ${canvasId}, size ${size}`,
          );
          throw new Error('Generated thumbnail is empty (matches empty thumbnail pattern)');
        }

        // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Минимальный размер dataURL для валидной PNG
        // Пустая PNG обычно меньше 200 байт в base64, валидная миниатюра должна быть больше
        if (dataURL.length < 200) {
          console.warn(
            `⚠️ [generateThumbnail] Thumbnail dataURL too short (${dataURL.length} chars) for canvas ${canvasId}`,
          );
          throw new Error('Generated thumbnail dataURL is too short to be valid');
        }

        // Обновляем статус миниатюры в хранилище
        get().updateThumbnailStatus(canvasId, size, 'loaded', dataURL);

        return dataURL;
      } catch (toDataURLError) {
        // Обработка ошибок генерации dataURL
        const maxRetries = 2; // Максимум 2 повторные попытки
        const shouldRetry =
          retryAttempt < maxRetries &&
          !(
            toDataURLError instanceof Error &&
            toDataURLError.message.includes('Tainted canvases may not be exported')
          ) &&
          !(
            toDataURLError instanceof Error &&
            toDataURLError.message.includes('Invalid canvas dimensions')
          ) &&
          !(
            toDataURLError instanceof Error &&
            toDataURLError.message.includes('No data for thumbnail generation')
          );

        if (shouldRetry) {
          console.warn(
            `⚠️ [generateThumbnail] Retry attempt ${retryAttempt + 1}/${maxRetries} for canvas ${canvasId}, size ${size}`,
            {
              error:
                toDataURLError instanceof Error ? toDataURLError.message : String(toDataURLError),
            },
          );

          // Задержка перед повторной попыткой (увеличивается с каждой попыткой)
          const retryDelay = (retryAttempt + 1) * 500; // 500ms, 1000ms, 1500ms...
          await new Promise((resolve) => setTimeout(resolve, retryDelay));

          // Повторная попытка генерации
          return get().generateThumbnail(
            canvasId,
            size,
            forceRegenerate,
            useInitialDataOnly,
            retryAttempt + 1,
          );
        }

        // Если не можем повторить - устанавливаем статус ошибки
        if (!hasExistingThumbnail) {
          get().updateThumbnailStatus(canvasId, size, 'error', '');
        }
        if (
          toDataURLError instanceof Error &&
          toDataURLError.message.includes('Tainted canvases may not be exported')
        ) {
          console.warn(
            `Slide ${canvasId} contains protected images (CORS), thumbnail cannot be generated.`,
          );
        }
        return hasExistingThumbnail ? currentThumbnailData.url : null;
      } finally {
        // ЗАЩИТА: Безопасно очищаем временный канвас и DOM элемент
        try {
          if (tempCanvas) {
            // Очищаем объекты перед dispose
            try {
              tempCanvas.clear();
            } catch (clearError) {
              console.warn('⚠️ [generateThumbnail] Error clearing temp canvas:', clearError);
            }

            // Dispose canvas
            if ((tempCanvas as any).dispose) {
              tempCanvas.dispose();
            }
          }
        } catch (disposeError) {
          console.warn('⚠️ [generateThumbnail] Error disposing temp canvas:', {
            canvasId,
            error: disposeError,
          });
        }

        // УДАЛЕНИЕ DOM ЭЛЕМЕНТА: Удаляем временный canvas элемент из DOM
        try {
          if (tempCanvasElement && tempCanvasElement.parentNode) {
            tempCanvasElement.parentNode.removeChild(tempCanvasElement);
          }
        } catch (removeError) {
          console.warn('⚠️ [generateThumbnail] Error removing temp canvas element:', {
            canvasId,
            error: removeError,
          });
        }
      }
    } catch (error) {
      // Обработка общих ошибок генерации
      const maxRetries = 2; // Максимум 2 повторные попытки
      const errorMessage = error instanceof Error ? error.message : String(error);
      const shouldRetry =
        retryAttempt < maxRetries &&
        !errorMessage.includes('Invalid canvas dimensions') &&
        !errorMessage.includes('No data for thumbnail generation') &&
        !errorMessage.includes('Failed to get canvas element context') &&
        !errorMessage.includes('Failed to initialize temporary canvas');

      if (shouldRetry) {
        console.warn(
          `⚠️ [generateThumbnail] Retry attempt ${retryAttempt + 1}/${maxRetries} for canvas ${canvasId}, size ${size} (general error)`,
          {
            error: errorMessage,
          },
        );

        // Задержка перед повторной попыткой
        const retryDelay = (retryAttempt + 1) * 500;
        await new Promise((resolve) => setTimeout(resolve, retryDelay));

        // Повторная попытка генерации
        return get().generateThumbnail(
          canvasId,
          size,
          forceRegenerate,
          useInitialDataOnly,
          retryAttempt + 1,
        );
      }

      console.error(`Error generating thumbnail:`, error);
      if (!hasExistingThumbnail) {
        get().updateThumbnailStatus(canvasId, size, 'error', '');
      }
      return hasExistingThumbnail ? currentThumbnailData.url : null;
    }
  },

  updateThumbnailStatus: (canvasId, size, status, url) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      const currentThumbnail = canvas.thumbnail || {
        smallImg: { status: 'pending', url: '' },
        bigImg: { status: 'pending', url: '' },
      };

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            thumbnail: {
              ...currentThumbnail,
              [size === 'small' ? 'smallImg' : 'bigImg']: {
                status,
                url,
              },
            },
          },
        },
      };
    });
  },

  queueThumbnailGeneration: (
    canvasId,
    size,
    forceRegenerate = false,
    priority = 0,
    useInitialDataOnly = false,
  ) => {
    const existingIndex = get().thumbnailQueue.findIndex(
      (item) => item.canvasId === canvasId && item.size === size,
    );

    if (existingIndex >= 0) {
      if (forceRegenerate || priority > get().thumbnailQueue[existingIndex].priority) {
        set((state) => {
          const updatedQueue = [...state.thumbnailQueue];
          updatedQueue[existingIndex] = {
            ...updatedQueue[existingIndex],
            forceRegenerate: forceRegenerate || updatedQueue[existingIndex].forceRegenerate,
            priority: Math.max(priority, updatedQueue[existingIndex].priority),
            useInitialDataOnly:
              useInitialDataOnly || updatedQueue[existingIndex].useInitialDataOnly,
            createdAt: Date.now(),
          };
          return { thumbnailQueue: updatedQueue };
        });
      }
      return;
    }

    set((state) => ({
      thumbnailQueue: [
        ...state.thumbnailQueue,
        {
          canvasId,
          size,
          forceRegenerate,
          priority,
          createdAt: Date.now(),
          useInitialDataOnly,
        },
      ],
    }));

    if (!get().isProcessingThumbnail) {
      get().processNextThumbnail();
    }
  },

  processNextThumbnail: async () => {
    const state = get();

    if (state.thumbnailQueue.length === 0) {
      set({ isProcessingThumbnail: false });
      return;
    }

    set({ isProcessingThumbnail: true });

    const sortedQueue = [...state.thumbnailQueue].sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.createdAt - b.createdAt;
    });

    const nextTask = sortedQueue[0];

    set((state) => ({
      thumbnailQueue: state.thumbnailQueue.filter(
        (item) => !(item.canvasId === nextTask.canvasId && item.size === nextTask.size),
      ),
    }));

    try {
      await get().generateThumbnail(
        nextTask.canvasId,
        nextTask.size,
        nextTask.forceRegenerate,
        nextTask.useInitialDataOnly,
      );
    } catch (error) {
      console.error('Error processing thumbnail:', error);
    }

    // Задержка 100ms между обработкой тумбнейлов даёт браузеру время
    // на сборку мусора canvas-контекстов предыдущего тумбнейла
    setTimeout(() => {
      get().processNextThumbnail();
    }, 100);
  },

  // clearing
  clearStore: () => {
    set({ ...defaultState });
  },

  cleanUpCanvas: (canvasId: string, clearTimers = false) => {
    // console.debug('🧹 [cleanUpCanvas] Cleaning up canvas:', { canvasId, clearTimers });

    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fabricInstance, element, ...restCanvas } = canvas;

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...restCanvas,
            fabricInstance: null,
            element: null,
            isNeedUpdateFabricInstance: true,
            isNeedUpdateElement: true,
          },
        },
      };
    });

    // Очищаем таймеры только при полном уничтожении компонента
    if (clearTimers) {
      // console.debug('🗑️ [cleanUpCanvas] Clearing timers for:', canvasId);
      const thumbnailTimerId = `thumbnail_${canvasId}`;
      if (get().thumbnailTimers.has(thumbnailTimerId)) {
        clearTimeout(get().thumbnailTimers.get(thumbnailTimerId));
        get().thumbnailTimers.delete(thumbnailTimerId);
      }

      if (get().saveBackupTimers.has(thumbnailTimerId)) {
        clearTimeout(get().saveBackupTimers.get(thumbnailTimerId));
        get().saveBackupTimers.delete(thumbnailTimerId);
      }
    } else {
      // console.debug('⏳ [cleanUpCanvas] Preserving timers for:', canvasId);
    }
  },

  // methods for blocking/unblocking thumbnail processing
  blockThumbnailProcessing: () => {
    set({ isProcessingThumbnail: true });
  },

  unblockThumbnailProcessing: () => {
    set({ isProcessingThumbnail: false });
    // Сразу запустить обработку очереди, если есть задачи
    if (get().thumbnailQueue.length > 0) {
      get().processNextThumbnail();
    }
  },

  clearBackups: (canvasId) => {
    set((state) => {
      const canvas = state.canvasData[canvasId];
      if (!canvas) return state;

      // console.debug(`🧹 [CanvasStore] Clearing backups for canvas: ${canvasId}`);

      return {
        canvasData: {
          ...state.canvasData,
          [canvasId]: {
            ...canvas,
            backups: [],
            currentStateIndex: 0,
            savedLastVersionStatus: {
              isSaved: true,
              isSaving: false,
              error: null,
            },
            isPendingUpdate: false,
          },
        },
      };
    });
  },

  // Методы для работы с глобальной историей
  addToGlobalHistory: (action) => {
    set((state) => {
      const { globalHistory } = state;

      // Обрезаем историю если мы находимся не в конце (для поддержки redo)
      const truncatedActions = globalHistory.actions.slice(0, globalHistory.currentActionIndex + 1);
      const newActions = [...truncatedActions, action];

      // console.debug('📚 [GlobalHistory] Adding action:', action);

      return {
        globalHistory: {
          actions: newActions,
          currentActionIndex: newActions.length - 1,
        },
      };
    });
  },

  canGlobalUndo: () => {
    const { globalHistory } = get();

    if (globalHistory.currentActionIndex < 0) {
      return false;
    }

    // ✅ НОВАЯ ЛОГИКА: Проверяем контекст стиля
    const context = get().detectUndoContext();

    if (context === 'style_rollback') {
      // Можем откатиться к старому стилю, если есть метка style_changed
      for (let i = globalHistory.currentActionIndex; i >= 0; i--) {
        const action = globalHistory.actions[i];
        if (action.type === 'style_boundary' && action.boundaryType === 'style_changed') {
          return true;
        }
      }
      return false;
    }

    // Проверяем реальную возможность выполнения действия
    const currentAction = globalHistory.actions[globalHistory.currentActionIndex];

    switch (currentAction.type) {
      case 'slide_backup': {
        const canvasData = get().canvasData[currentAction.slideId];
        return canvasData && canvasData.currentStateIndex > 0;
      }

      case 'slide_switch': {
        // Переключение слайдов всегда возможно если есть предыдущее действие
        return globalHistory.currentActionIndex > 0;
      }

      case 'slide_add':
      case 'slide_remove': {
        // Пока не реализованы, возвращаем false
        return false;
      }

      case 'style_boundary': {
        // Стилевые границы можно "откатить"
        return true;
      }

      default:
        return false;
    }
  },

  canGlobalRedo: () => {
    const { globalHistory } = get();

    if (globalHistory.currentActionIndex >= globalHistory.actions.length - 1) {
      return false;
    }

    // ✅ НОВАЯ ЛОГИКА: Проверяем контекст стиля для redo
    const context = get().detectUndoContext();

    if (context === 'style_forward') {
      // Можем перейти к новому стилю, если есть метка restyling_done
      for (let i = globalHistory.currentActionIndex + 1; i < globalHistory.actions.length; i++) {
        const action = globalHistory.actions[i];
        if (action.type === 'style_boundary' && action.boundaryType === 'restyling_done') {
          return true;
        }
      }
      return false;
    }

    // Проверяем реальную возможность выполнения действия
    const nextActionIndex = globalHistory.currentActionIndex + 1;
    const nextAction = globalHistory.actions[nextActionIndex];

    switch (nextAction.type) {
      case 'slide_backup': {
        const canvasData = get().canvasData[nextAction.slideId];
        return canvasData && canvasData.currentStateIndex < canvasData.backups.length - 1;
      }

      case 'slide_switch': {
        // Переключение слайдов всегда возможно
        return true;
      }

      case 'slide_add':
      case 'slide_remove': {
        // Пока не реализованы, возвращаем false
        return false;
      }

      case 'style_boundary': {
        // Стилевые границы можно "восстановить"
        return true;
      }

      default:
        return false;
    }
  },

  clearGlobalHistory: () => {
    set(() => ({
      globalHistory: {
        actions: [],
        currentActionIndex: -1,
      },
    }));
  },

  // Методы для работы со стилевыми границами
  detectUndoContext: () => {
    const { globalHistory } = get();
    const currentIndex = globalHistory.currentActionIndex;

    // Ищем ближайшие метки
    let nearestStyleChanged = -1;
    let nearestRestylungDone = -1;

    for (let i = currentIndex; i >= 0; i--) {
      const action = globalHistory.actions[i];
      if (action.type === 'style_boundary') {
        if (action.boundaryType === 'restyling_done' && nearestRestylungDone === -1) {
          nearestRestylungDone = i;
        }
        if (action.boundaryType === 'style_changed' && nearestStyleChanged === -1) {
          nearestStyleChanged = i;
          break; // style_changed всегда раньше
        }
      }
    }

    // Определяем контекст
    if (nearestRestylungDone !== -1 && currentIndex === nearestRestylungDone) {
      return 'style_rollback'; // На метке restyling_done - rollback к старому стилю
    }

    if (nearestStyleChanged !== -1 && currentIndex === nearestStyleChanged) {
      return 'style_forward'; // На метке style_changed - forward к новому стилю
    }

    // Проверяем, находимся ли в "зоне нового стиля" (между метками)
    if (nearestRestylungDone !== -1 && nearestStyleChanged !== -1) {
      if (currentIndex > nearestStyleChanged && currentIndex < nearestRestylungDone) {
        // В переходной зоне - проверяем близость к границе
        const distanceToStyleChanged = currentIndex - nearestStyleChanged;
        if (distanceToStyleChanged <= 3) {
          // Близко к границе старого стиля
          return 'style_rollback';
        }
      }
    }

    return 'normal';
  },

  findLastBackupBeforeBoundary: (slideId: string, boundaryIndex: number) => {
    const { globalHistory } = get();

    // Ищем последний backup для данного слайда ДО указанной границы
    for (let i = boundaryIndex - 1; i >= 0; i--) {
      const action = globalHistory.actions[i];
      if (action.type === 'slide_backup' && action.slideId === slideId) {
        const canvasData = get().canvasData[slideId];
        if (canvasData && canvasData.backups[action.backupIndex]) {
          return canvasData.backups[action.backupIndex];
        }
      }
    }

    return null;
  },

  restoreSlideToBackup: async (slideId: string, backupData: any) => {
    const canvasData = get().canvasData[slideId];
    if (!canvasData || !canvasData.fabricInstance) {
      console.warn(`⚠️ [restoreSlideToBackup] Canvas ${slideId} not found or not active`);
      return;
    }

    const fabricInstance = canvasData.fabricInstance;

    try {
      await loadObjectsWithLayers(fabricInstance, slideId, backupData);
      fabricInstance.renderAll();

      // Обновляем состояние в store
      set((state) => {
        const canvas = state.canvasData[slideId];
        if (!canvas) return state;

        // Находим индекс этого бэкапа в массиве backups
        const backupIndex = canvas.backups.findIndex(
          (backup) => JSON.stringify(backup) === JSON.stringify(backupData),
        );

        return {
          canvasData: {
            ...state.canvasData,
            [slideId]: {
              ...canvas,
              currentStateIndex: backupIndex >= 0 ? backupIndex : canvas.currentStateIndex,
            },
          },
        };
      });

      // console.debug(`✅ [restoreSlideToBackup] Slide ${slideId} restored to backup`);
    } catch (error) {
      console.error(`❌ [restoreSlideToBackup] Error restoring slide ${slideId}:`, error);
    }
  },

  undoToStyleBoundary: async (boundaryType: 'style_changed' | 'restyling_done') => {
    const { globalHistory } = get();

    // Ищем метку
    let boundaryIndex = -1;
    for (let i = globalHistory.currentActionIndex; i >= 0; i--) {
      const action = globalHistory.actions[i];
      if (action.type === 'style_boundary' && action.boundaryType === boundaryType) {
        boundaryIndex = i;
        break;
      }
    }

    if (boundaryIndex === -1) {
      console.warn(`⚠️ [undoToStyleBoundary] Boundary ${boundaryType} not found`);
      return;
    }

    const boundary = globalHistory.actions[boundaryIndex] as StyleBoundaryAction;

    // console.debug('🎨 [undoToStyleBoundary] Rolling back to:', boundary);

    set({ isUndoOrRedoInProgress: true });

    try {
      // Для каждого затронутого слайда возвращаем к состоянию ДО границы
      for (const slideId of boundary.affectedSlides) {
        const lastBackupBeforeBoundary = get().findLastBackupBeforeBoundary(slideId, boundaryIndex);
        if (lastBackupBeforeBoundary) {
          await get().restoreSlideToBackup(slideId, lastBackupBeforeBoundary);
        }
      }

      // Устанавливаем глобальный индекс на позицию ДО границы
      set((state) => ({
        globalHistory: {
          ...state.globalHistory,
          currentActionIndex: boundaryIndex - 1,
        },
      }));

      // console.debug('✅ [undoToStyleBoundary] Style rollback completed');
    } finally {
      set({ isUndoOrRedoInProgress: false });
    }
  },

  redoToStyleBoundary: async (boundaryType: 'style_changed' | 'restyling_done') => {
    const { globalHistory } = get();

    // Ищем метку после текущей позиции
    let boundaryIndex = -1;
    for (let i = globalHistory.currentActionIndex + 1; i < globalHistory.actions.length; i++) {
      const action = globalHistory.actions[i];
      if (action.type === 'style_boundary' && action.boundaryType === boundaryType) {
        boundaryIndex = i;
        break;
      }
    }

    if (boundaryIndex === -1) {
      console.warn(`⚠️ [redoToStyleBoundary] Boundary ${boundaryType} not found`);
      return;
    }

    set({ isUndoOrRedoInProgress: true });

    try {
      // Устанавливаем глобальный индекс на позицию границы
      set((state) => ({
        globalHistory: {
          ...state.globalHistory,
          currentActionIndex: boundaryIndex,
        },
      }));

      // console.debug('✅ [redoToStyleBoundary] Style forward completed');
    } finally {
      set({ isUndoOrRedoInProgress: false });
    }
  },

  globalUndo: async () => {
    const { globalHistory, isUndoOrRedoInProgress } = get();

    if (isUndoOrRedoInProgress || globalHistory.currentActionIndex < 0) {
      return;
    }

    // Детекция контекста стиля
    const context = get().detectUndoContext();

    // console.debug('🎨 [GlobalUndo] Context detected:', context);

    switch (context) {
      case 'style_rollback':
        return await get().undoToStyleBoundary('style_changed');

      case 'normal':
      default:
        // Существующая логика
        break;
    }

    set({ isUndoOrRedoInProgress: true });

    try {
      const currentAction = globalHistory.actions[globalHistory.currentActionIndex];
      // console.debug('⬅️ [GlobalUndo] Processing action:', currentAction);

      let actionWasExecuted = false; // Флаг для отслеживания успешного выполнения

      switch (currentAction.type) {
        case 'slide_backup': {
          const canvasData = get().canvasData[currentAction.slideId];
          if (canvasData && canvasData.currentStateIndex > 0) {
            // Откатываемся на предыдущий бэкап в рамках слайда
            const result = await get().undo(currentAction.slideId, true);
            actionWasExecuted = result !== null; // undo возвращает null если не может выполниться
          }
          break;
        }

        case 'slide_switch': {
          // Находим предыдущее действие чтобы понять на какой слайд переключиться
          if (globalHistory.currentActionIndex > 0) {
            const prevAction = globalHistory.actions[globalHistory.currentActionIndex - 1];
            if (prevAction.type === 'slide_backup' || prevAction.type === 'slide_switch') {
              // Переключаемся на предыдущий слайд без записи в историю
              set({ activeCanvasId: prevAction.slideId });
              actionWasExecuted = true;
            }
          }
          break;
        }

        case 'slide_add': {
          // Удаляем добавленный слайд (понадобится интеграция с SlideStore)
          // console.debug('⬅️ [GlobalUndo] Removing added slide:', currentAction.slideId);
          // Здесь нужно будет интегрироваться с useSlideStore
          // actionWasExecuted = true; // Раскомментировать после реализации
          break;
        }

        case 'slide_remove': {
          // Восстанавливаем удаленный слайд
          // console.debug('⬅️ [GlobalUndo] Restoring removed slide:', currentAction.slideId);
          // Здесь нужно будет интегрироваться с useSlideStore
          // actionWasExecuted = true; // Раскомментировать после реализации
          break;
        }

        case 'style_boundary': {
          // Стилевые границы обрабатываются отдельно, здесь просто пропускаем
          actionWasExecuted = true;
          break;
        }
      }

      // Обновляем индекс в глобальной истории ТОЛЬКО если действие было успешно выполнено
      if (actionWasExecuted) {
        set((state) => ({
          globalHistory: {
            ...state.globalHistory,
            currentActionIndex: state.globalHistory.currentActionIndex - 1,
          },
        }));
        // console.debug('✅ [GlobalUndo] Action executed successfully, global index decremented');
      } else {
        // console.debug('⚠️ [GlobalUndo] Action could not be executed, global index unchanged');
      }
    } catch (error) {
      console.error('❌ [GlobalUndo] Error:', error);
    } finally {
      set({ isUndoOrRedoInProgress: false });
    }
  },

  globalRedo: async () => {
    const { globalHistory, isUndoOrRedoInProgress } = get();

    if (
      isUndoOrRedoInProgress ||
      globalHistory.currentActionIndex >= globalHistory.actions.length - 1
    ) {
      return;
    }

    //  Детекция контекста стиля для redo
    const context = get().detectUndoContext();

    // console.debug('🎨 [GlobalRedo] Context detected:', context);

    switch (context) {
      case 'style_forward':
        return await get().redoToStyleBoundary('restyling_done');

      case 'normal':
      default:
        // Существующая логика
        break;
    }

    set({ isUndoOrRedoInProgress: true });

    try {
      const nextActionIndex = globalHistory.currentActionIndex + 1;
      const nextAction = globalHistory.actions[nextActionIndex];
      // console.debug('➡️ [GlobalRedo] Processing action:', nextAction);

      let actionWasExecuted = false; // Флаг для отслеживания успешного выполнения

      switch (nextAction.type) {
        case 'slide_backup': {
          const canvasData = get().canvasData[nextAction.slideId];
          if (canvasData && canvasData.currentStateIndex < canvasData.backups.length - 1) {
            // Возвращаемся к следующему бэкапу в рамках слайда
            const result = await get().redo(nextAction.slideId, true);
            actionWasExecuted = result !== null; // redo возвращает null если не может выполниться
          }
          break;
        }

        case 'slide_switch': {
          // Переключаемся на указанный слайд
          set({ activeCanvasId: nextAction.slideId });
          actionWasExecuted = true;
          break;
        }

        case 'slide_add': {
          // Восстанавливаем добавленный слайд
          // console.debug('➡️ [GlobalRedo] Restoring added slide:', nextAction.slideId);
          // Здесь нужно будет интегрироваться с useSlideStore
          // actionWasExecuted = true; // Раскомментировать после реализации
          break;
        }

        case 'slide_remove': {
          // Повторно удаляем слайд
          // console.debug('➡️ [GlobalRedo] Re-removing slide:', nextAction.slideId);
          // Здесь нужно будет интегрироваться с useSlideStore
          // actionWasExecuted = true; // Раскомментировать после реализации
          break;
        }

        case 'style_boundary': {
          // Стилевые границы обрабатываются отдельно, здесь просто пропускаем
          actionWasExecuted = true;
          break;
        }
      }

      // ✅ ИСПРАВЛЕНИЕ: Обновляем индекс в глобальной истории ТОЛЬКО если действие было успешно выполнено
      if (actionWasExecuted) {
        set((state) => ({
          globalHistory: {
            ...state.globalHistory,
            currentActionIndex: nextActionIndex,
          },
        }));
        // console.debug('✅ [GlobalRedo] Action executed successfully, global index incremented');
      } else {
        // console.debug('⚠️ [GlobalRedo] Action could not be executed, global index unchanged');
      }
    } catch (error) {
      console.error('❌ [GlobalRedo] Error:', error);
    } finally {
      set({ isUndoOrRedoInProgress: false });
    }
  },

  // Методы для работы с персистентностью
  saveWorkspaceToStorage: (workspaceId: string) => {
    if (!workspaceId) return;

    const state = get();
    const currentTime = Date.now();

    // Подготавливаем данные для сохранения
    const persistentCanvasData: { [canvasId: string]: PersistedCanvasData } = {};

    Object.entries(state.canvasData).forEach(([canvasId, canvasData]) => {
      persistentCanvasData[canvasId] = {
        backups: canvasData.backups,
        currentStateIndex: canvasData.currentStateIndex,
        metaData: canvasData.metaData,
        // thumbnail: canvasData.thumbnail,
        position: canvasData.position,
        lastUpdated: currentTime,
        initialData: canvasData.initialData, // Сохраняем initialData
        markerType: canvasData.markerType, // Сохраняем markerType
      };
    });

    const workspaceData: WorkspacePersistentData = {
      canvasData: persistentCanvasData,
      globalHistory: state.globalHistory,
      lastAccessed: currentTime,
    };

    // Асинхронное сохранение в IndexedDB
    (async () => {
      try {
        // Получаем текущие данные из IndexedDB
        const existingData = await canvasIndexedDB.getItem('canvas-workspace-storage');
        const storage: IndexedDBStorage = existingData
          ? JSON.parse(existingData)
          : { workspaces: {} };

        // Обновляем данные для текущего workspace
        storage.workspaces[workspaceId] = workspaceData;

        // Сохраняем обратно в IndexedDB
        await canvasIndexedDB.setItem('canvas-workspace-storage', JSON.stringify(storage));

        console.debug('✅ [Persist] Workspace saved to IndexedDB:', {
          workspaceId,
          canvasCount: Object.keys(persistentCanvasData).length,
          globalHistoryActions: state.globalHistory.actions.length,
        });
      } catch (error) {
        console.error('❌ [Persist] Error saving workspace to IndexedDB:', error);
      }
    })();
  },

  loadWorkspaceFromStorage: async (workspaceId: string): Promise<boolean> => {
    if (!workspaceId) return false;

    try {
      const storageData = await canvasIndexedDB.getItem('canvas-workspace-storage');
      if (!storageData) return false;

      const storage: IndexedDBStorage = JSON.parse(storageData);
      const workspaceData = storage.workspaces[workspaceId];

      if (!workspaceData) return false;

      // Получаем текущие данные слайдов для сравнения
      const slideStore = useSlideStore.getState();
      const currentSlides = slideStore.slides;

      // Восстанавливаем состояние
      set((state) => {
        const restoredCanvasData: CanvasDataList = {};

        // Восстанавливаем данные канвасов
        Object.entries(workspaceData.canvasData).forEach(([canvasId, persistedData]) => {
          const typedPersistedData = persistedData as PersistedCanvasData;
          const existingCanvas = state.canvasData[canvasId];

          // Проверяем, изменился ли конфиг слайда по сравнению с сохраненным
          const currentSlide = currentSlides.find((s) => s.id.toString() === canvasId);
          const hasNewConfig =
            currentSlide?.config &&
            JSON.stringify(currentSlide.config) !== JSON.stringify(typedPersistedData.initialData);

          restoredCanvasData[canvasId] = {
            // Базовые поля из defaultState или существующих данных
            initialData: typedPersistedData.initialData || existingCanvas?.initialData || null,
            fabricInstance: null, // Будет установлен при создании канваса
            element: null, // Будет установлен при создании канваса
            backupDepth: existingCanvas?.backupDepth || 1500,
            canvasDimensions: existingCanvas?.canvasDimensions || CANVAS_DIMENSIONS,
            canvasScale: existingCanvas?.canvasScale || CANVAS_SCALE,
            savedLastVersionStatus: {
              isSaved: true,
              isSaving: false,
              error: null,
            },
            isPendingUpdate: false,
            staticCanvas: existingCanvas?.staticCanvas,
            isNeedUpdateFabricInstance: true, // Потребуется обновление
            isNeedUpdateElement: true, // Потребуется обновление
            isRegistered: false, // Потребуется регистрация
            isRelayout: false,
            isRepaint: false,
            movementState: {
              lastDirection: null,
              hasPendingChanges: false,
              activeObjects: [],
            },
            // Восстановленные данные
            backups: typedPersistedData.backups,
            currentStateIndex: typedPersistedData.currentStateIndex,
            layersMap: {
              // Init layersMap for every canvas
              background: [],
              middle: [],
              foreground: [],
              overlay: [],
            } as LayersMap,
            metaData: typedPersistedData.metaData,
            // НЕ восстанавливаем thumbnails если изменился конфиг
            thumbnail: hasNewConfig
              ? {
                  smallImg: { status: 'pending' as const, url: '' },
                  bigImg: { status: 'pending' as const, url: '' },
                }
              : typedPersistedData.thumbnail,
            position: typedPersistedData.position,
            // Восстанавливаем markerType из сохраненных данных или из initialData
            markerType:
              typedPersistedData.markerType ||
              typedPersistedData.initialData?.extra?.marker_type ||
              'normal',
          };
        });

        return {
          canvasData: { ...state.canvasData, ...restoredCanvasData },
          globalHistory: workspaceData.globalHistory,
        };
      });

      // Обновляем время последнего доступа
      storage.workspaces[workspaceId].lastAccessed = Date.now();
      await canvasIndexedDB.setItem('canvas-workspace-storage', JSON.stringify(storage));

      return true;
    } catch (error) {
      console.error('❌ [Persist] Error loading workspace from storage:', error);
      return false;
    }
  },

  clearWorkspaceStorage: async (workspaceId: string) => {
    if (!workspaceId) return;

    // Асинхронная очистка IndexedDB
    (async () => {
      try {
        // Полная очистка всего ключа canvas-workspace-storage
        await canvasIndexedDB.removeItem('canvas-workspace-storage');

        console.debug(
          '🗑️ [Persist] Complete workspace storage cleared (all workspaces):',
          workspaceId,
        );
      } catch (error) {
        console.error('❌ [Persist] Error clearing workspace storage:', error);
      }
    })();
  },

  cleanupOldWorkspaces: async (maxAge: number = 7 * 24 * 60 * 60 * 1000) => {
    // 7 дней по умолчанию
    // Асинхронная очистка
    (async () => {
      try {
        const storageData = await canvasIndexedDB.getItem('canvas-workspace-storage');
        if (!storageData) return;

        const storage: IndexedDBStorage = JSON.parse(storageData);
        const currentTime = Date.now();
        let cleanedCount = 0;

        Object.entries(storage.workspaces).forEach(([workspaceId, workspaceData]) => {
          if (currentTime - workspaceData.lastAccessed > maxAge) {
            delete storage.workspaces[workspaceId];
            cleanedCount++;
          }
        });

        if (cleanedCount > 0) {
          await canvasIndexedDB.setItem('canvas-workspace-storage', JSON.stringify(storage));
          console.debug('🧹 [Persist] Cleaned old workspaces:', { cleanedCount, maxAge });
        }
      } catch (error) {
        console.error('❌ [Persist] Error cleaning old workspaces:', error);
      }
    })();
  },
}));
